import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Pause, RotateCcw, Volume2, VolumeX, Maximize2, Minimize2, Users, Settings } from 'lucide-react';
import { Button } from './ui/button';
import useGameEngine from '../hooks/useGameEngine';
import useMultiplayerSync from '../hooks/useMultiplayerSync';
import TouchControls from './TouchControls';
import Cutscene from './Cutscene';
import SoundSettings from './SoundSettings';
import { useWallet } from '@solana/wallet-adapter-react';

const TOKEN_CONTRACT = 'E6tBfgKTVCZ3ziP2ZB5V1acDZu6mwA6Ho4jVPVg3pump';

const GameCanvas = ({ 
  playerColor = '#FF3B30', 
  playerSpriteUrl = null,
  nftBonuses = null,
  nftName = null,
  nftId = null,
  onGameOver, 
  onScoreUpdate,
  isActive = true,
  autoStart = false,
  tokenBalance = 0,
  ownedWeapons = [],
  isMultiplayer = false,
  lobbyId = null,
  gameSessionId = null,
  multiplayerLobby = null,
  isTournament = false,
  tournamentTimeLimit = 120
}) => {
  const { publicKey } = useWallet();
  // Use the same persistent guest ID that MultiplayerLobby/LobbyBrowser use, so the
  // WS server can match this connection back to the lobby player record.
  const walletAddress = publicKey?.toBase58() || (() => {
    let g = sessionStorage.getItem('metal_lego_guest_id');
    if (!g) {
      g = 'guest_' + Math.random().toString(36).substr(2, 8);
      sessionStorage.setItem('metal_lego_guest_id', g);
    }
    return g;
  })();
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [showTouchControls, setShowTouchControls] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [showRotatePrompt, setShowRotatePrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [showCutscene, setShowCutscene] = useState(false);
  const [cutsceneLevel, setCutsceneLevel] = useState(0);
  const [showIntroVideo, setShowIntroVideo] = useState(false);
  const [videoFading, setVideoFading] = useState('none'); // 'none' | 'in' | 'visible' | 'out'
  const hasAutoStarted = useRef(false);
  const hasShownPrologue = useRef(false);
  const videoRef = useRef(null);
  
  // Intro video URL - hosted locally for reliable playback
  const INTRO_VIDEO_URL = "/intro_video.mp4";
  
  // Track isPlaying in separate state to avoid TDZ with hook ordering
  const [isGamePlaying, setIsGamePlaying] = useState(false);

  // Snapshot of peer scores captured at the moment game ends — survives the
  // 5s peer-state cleanup so the co-op breakdown stays populated on game-over.
  const [finalPeerScores, setFinalPeerScores] = useState(null);

  // Multiplayer sync hook (uses isGamePlaying instead of gameState.isPlaying)
  const multiplayerSync = useMultiplayerSync(
    lobbyId,
    walletAddress,
    isMultiplayer && isGamePlaying
  );
  
  const { gameState, startGame, pauseGame, stopGame, setKeys } = useGameEngine(
    canvasRef,
    playerColor,
    onGameOver,
    onScoreUpdate,
    ownedWeapons,
    playerSpriteUrl,
    nftBonuses,
    nftId,
    isMultiplayer ? multiplayerSync : null,
    isTournament ? tournamentTimeLimit : 0
  );

  // Sync isPlaying state for multiplayer hook
  useEffect(() => {
    setIsGamePlaying(gameState.isPlaying);
  }, [gameState.isPlaying]);

  // Detect touch device and orientation
  useEffect(() => {
    const checkDevice = () => {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsTouchDevice(hasTouch);
      
      const landscape = window.innerWidth > window.innerHeight;
      setIsLandscape(landscape);
      
      // Rotate prompt disabled - allow play in any orientation
      setShowRotatePrompt(false);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    window.addEventListener('orientationchange', checkDevice);
    
    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
    };
  }, []);

  // Handle screen lock for landscape
  useEffect(() => {
    const lockOrientation = async () => {
      if (screen.orientation && screen.orientation.lock) {
        try {
          await screen.orientation.lock('landscape');
        } catch (e) {
          // Orientation lock not supported or not allowed
          console.log('Orientation lock not available');
        }
      }
    };

    if (gameState.isPlaying && isTouchDevice) {
      lockOrientation();
    }

    return () => {
      if (screen.orientation && screen.orientation.unlock) {
        try {
          screen.orientation.unlock();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, [gameState.isPlaying, isTouchDevice]);

  // Loading simulation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Function to start game with countdown
  const startWithCountdown = useCallback(() => {
    if (isStarting) return;
    setIsStarting(true);
    setCountdown(3);
    
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) {
          clearInterval(countdownInterval);
          setTimeout(() => {
            setCountdown(null);
            setIsStarting(false);
            startGame();
          }, 800);
          return 'GO!';
        }
        return prev - 1;
      });
    }, 800);
  }, [startGame, isStarting]);

  // Show prologue cutscene on first load (before game starts)
  useEffect(() => {
    if (!isLoading && !hasShownPrologue.current && autoStart) {
      hasShownPrologue.current = true;
      setCutsceneLevel(0);
      setShowCutscene(true);
    }
  }, [isLoading, autoStart]);

  // Show cutscene when level transition starts (disabled for quick transitions)
  // Cutscenes are now only shown at prologue or special story moments
  // The quick cut screen handles level transitions smoothly without interruption

  // Handle cutscene completion
  const handleCutsceneComplete = useCallback(() => {
    setShowCutscene(false);
    
    if (cutsceneLevel === 0 && !gameState.isPlaying) {
      // Prologue finished - show intro video with fade in
      setShowIntroVideo(true);
      setVideoFading('in');
      
      // After fade in animation (500ms), set to visible
      setTimeout(() => {
        setVideoFading('visible');
        // Auto-play the video
        if (videoRef.current) {
          videoRef.current.play().catch(e => console.log('Video autoplay blocked:', e));
        }
      }, 500);
    }
    // Level transition cutscenes are handled by the game engine timer
  }, [cutsceneLevel, gameState.isPlaying]);
  
  // Handle video end - fade out and start countdown
  const handleVideoEnd = useCallback(() => {
    setVideoFading('out');
    
    // After fade out (500ms), hide video and start countdown
    setTimeout(() => {
      setShowIntroVideo(false);
      setVideoFading('none');
      startWithCountdown();
    }, 500);
  }, [startWithCountdown]);
  
  // Allow skipping video with click/tap or ESC
  const handleSkipVideo = useCallback(() => {
    if (showIntroVideo && videoFading === 'visible') {
      handleVideoEnd();
    }
  }, [showIntroVideo, videoFading, handleVideoEnd]);

  // Countdown and auto-start (modified to work with cutscene)
  useEffect(() => {
    if (autoStart && !hasAutoStarted.current && !isLoading && canvasRef.current && !showCutscene) {
      // Only auto-start if prologue cutscene has been shown and not already playing
      if (hasShownPrologue.current && !gameState.isPlaying && !isStarting) {
        hasAutoStarted.current = true;
      }
    }
  }, [autoStart, isLoading, showCutscene, gameState.isPlaying, isStarting]);

  // Handle Play Again click
  const handlePlayAgain = useCallback(() => {
    startWithCountdown();
  }, [startWithCountdown]);

  // Show touch controls when game is playing
  useEffect(() => {
    if (gameState.isPlaying && !gameState.isPaused && !gameState.isGameOver) {
      if (isTouchDevice) setShowTouchControls(true);
    } else {
      setShowTouchControls(false);
    }
  }, [gameState.isPlaying, gameState.isPaused, gameState.isGameOver, isTouchDevice]);

  // Snapshot peer scores at game-over moment so the co-op breakdown survives
  // the 5s stale-peer cleanup in useMultiplayerSync.
  useEffect(() => {
    if (gameState.isGameOver && isMultiplayer && multiplayerSync?.otherPlayers) {
      const snap = {};
      Object.entries(multiplayerSync.otherPlayers).forEach(([wallet, p]) => {
        snap[wallet] = { score: p.score || 0, kills: p.kills || 0 };
      });
      if (Object.keys(snap).length > 0) {
        setFinalPeerScores(snap);
      }
    }
    if (!gameState.isGameOver) {
      setFinalPeerScores(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.isGameOver, isMultiplayer]);


  // Canvas resize handler
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current && containerRef.current) {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFullscreen]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(async () => {
    const gameContainer = containerRef.current?.parentElement;
    if (!gameContainer) return;

    try {
      if (!document.fullscreenElement) {
        await gameContainer.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (e) {
      console.log('Fullscreen not supported');
    }
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleMute = () => {
    import('../utils/SoundManager').then(module => {
      const soundManager = module.default;
      if (isMuted) {
        soundManager.setMasterVolume(0.5);
      } else {
        soundManager.setMasterVolume(0);
      }
      setIsMuted(!isMuted);
    });
  };

  // Touch control handlers (2.5D: joystick controls left/right/up/down depth)
  const handleMoveChange = useCallback((movement) => {
    if (setKeys) {
      setKeys(prev => ({
        ...prev,
        left: movement.left,
        right: movement.right,
        up: movement.up || false,
        down: movement.down || false
      }));
    }
  }, [setKeys]);

  const handleJump = useCallback(() => {
    if (setKeys) {
      setKeys(prev => ({ ...prev, jump: true }));
      setTimeout(() => {
        setKeys(prev => ({ ...prev, jump: false }));
      }, 100);
    }
  }, [setKeys]);

  const handleShootStart = useCallback(() => {
    if (setKeys) {
      setKeys(prev => ({ ...prev, shoot: true }));
    }
  }, [setKeys]);

  const handleShootEnd = useCallback(() => {
    if (setKeys) {
      setKeys(prev => ({ ...prev, shoot: false }));
    }
  }, [setKeys]);

  const handleGrenade = useCallback(() => {
    if (setKeys) {
      setKeys(prev => ({ ...prev, grenade: true }));
      setTimeout(() => {
        setKeys(prev => ({ ...prev, grenade: false }));
      }, 100);
    }
  }, [setKeys]);

  return (
    <div className={`w-full h-full flex flex-col ${isFullscreen ? 'fixed inset-0 z-40' : ''}`}>
      <div 
        ref={containerRef}
        className={`game-canvas-container relative ${isFullscreen ? 'fullscreen-game' : ''}`}
        data-testid="game-canvas-container"
        style={{ 
          width: '100%',
          height: isFullscreen ? '100vh' : (isTouchDevice ? (isLandscape ? '65vh' : '55vh') : '70vh'),
          maxWidth: isFullscreen ? '100vw' : '100%',
          maxHeight: isFullscreen ? '100vh' : '70vh',
          margin: isFullscreen ? '0' : '0 auto',
          border: isFullscreen ? 'none' : undefined,
          borderRadius: isFullscreen ? '0' : undefined,
        }}
      >
        <canvas 
          ref={canvasRef} 
          className="game-canvas"
          data-testid="game-canvas"
        />

        {/* Loading Screen */}
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black z-30">
            <div className="relative w-20 h-20 mb-6">
              {/* Spinning loader */}
              <div className="absolute inset-0 border-4 border-[#14F195]/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-transparent border-t-[#14F195] rounded-full animate-spin"></div>
              {/* Center icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-12 bg-[#FF3B30] rounded"></div>
              </div>
            </div>
            <p className="font-game text-[#14F195] text-sm animate-pulse">LOADING...</p>
            <div className="mt-4 w-48 h-2 bg-[#333] rounded-full overflow-hidden">
              <div className="h-full bg-[#14F195] rounded-full animate-loading-bar"></div>
            </div>
          </div>
        )}

        {/* Countdown Overlay */}
        {countdown !== null && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-30">
            <div className={`font-game text-6xl sm:text-8xl ${countdown === 'GO!' ? 'text-[#14F195] scale-125' : 'text-white'} transition-all duration-200 animate-pulse`}>
              {countdown}
            </div>
          </div>
        )}

        {/* Level Transition Screen - Boss Vanquished stinger → fadeOut → stats → fadeIn */}
        {gameState.showLevelTransition && (
          <div 
            className={`absolute inset-0 flex flex-col items-center justify-center z-30 p-4 transition-all duration-300
              ${gameState.transitionPhase === 'stinger' ? 'bg-black/40' : ''}
              ${gameState.transitionPhase === 'fadeOut' ? 'bg-black animate-pulse' : ''}
              ${gameState.transitionPhase === 'showStats' ? 'bg-black/95' : ''}
              ${gameState.transitionPhase === 'fadeIn' ? 'bg-black/70' : ''}
            `}
            data-testid="level-transition-overlay"
          >
            {/* Boss Vanquished stinger (first 0.6s) */}
            {gameState.transitionPhase === 'stinger' && (
              <div
                className="flex flex-col items-center justify-center"
                style={{ animation: 'stingerPop 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards' }}
                data-testid="boss-vanquished-stinger"
              >
                <div
                  className="font-game text-2xl sm:text-3xl mb-2 text-center tracking-[0.2em]"
                  style={{
                    color: '#FFD700',
                    textShadow: '0 0 12px #FFD700, 0 0 24px #FFD700, 0 0 40px #FF9500',
                    letterSpacing: '0.25em',
                  }}
                >
                  BOSS VANQUISHED
                </div>
                <div
                  className="font-game text-4xl sm:text-5xl text-center mb-3"
                  style={{
                    color: '#fff',
                    textShadow: '0 0 14px #FF3B30, 0 0 28px #FF3B30',
                  }}
                  data-testid="boss-vanquished-name"
                >
                  {gameState.defeatedBossName || 'BOSS'}
                </div>
                {gameState.defeatedBossReward != null && (
                  <div
                    className="font-game text-lg sm:text-xl px-4 py-1 rounded-full border-2"
                    style={{
                      color: '#14F195',
                      borderColor: '#14F195',
                      background: 'rgba(20,241,149,0.08)',
                      textShadow: '0 0 8px #14F195',
                    }}
                    data-testid="boss-vanquished-reward"
                  >
                    +{gameState.defeatedBossReward.toLocaleString()} PTS
                  </div>
                )}
              </div>
            )}

            {/* Content only visible during showStats phase */}
            <div className={`transition-opacity duration-200 ${gameState.transitionPhase === 'showStats' ? 'opacity-100' : 'opacity-0'}`}>
              <h2 className="font-game text-3xl sm:text-4xl text-[#14F195] mb-2 neon-text text-center animate-bounce">
                LEVEL {gameState.level} COMPLETE!
              </h2>
              <div className="font-game text-xl text-[#FFD700] mb-4 text-center">
                BOSS DEFEATED!
              </div>
              
              {/* Compact Stats Row */}
              <div className="glass-card p-4 mb-4">
                <div className="flex justify-center gap-8">
                  <div className="text-center">
                    <p className="text-[#A1A1AA] text-xs">SCORE</p>
                    <p className="font-game text-lg text-[#14F195]">{gameState.score.toLocaleString()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[#A1A1AA] text-xs">KILLS</p>
                    <p className="font-game text-lg text-[#FF3B30]">{gameState.kills}</p>
                  </div>
                </div>
              </div>

              {/* Next Level Indicator */}
              <div className="text-center">
                <p className="font-game text-2xl text-white animate-pulse">
                  LEVEL {gameState.level + 1} INCOMING...
                </p>
              </div>
            </div>
            
            {/* Quick progress indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-48 h-1 bg-[#333] rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#9945FF] rounded-full transition-all duration-100"
                style={{ width: `${((2.6 - parseFloat(gameState.levelTransitionTimer)) / 2.6) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* HUD Overlay */}
        {gameState.isPlaying && !gameState.isGameOver && countdown === null && !gameState.showLevelTransition && (
          <div className="game-hud" data-testid="game-hud">
            <div className="flex flex-col gap-1 sm:gap-2">
              <div className="hud-item text-[10px] sm:text-xs" data-testid="hud-score">
                SCORE: {gameState.score.toString().padStart(6, '0')}
              </div>
              <div className="hud-item text-[10px] sm:text-xs" data-testid="hud-level">
                LVL: {gameState.level}
              </div>
              {/* Combo Meter (P-1) — only when ≥3 consecutive kills */}
              {gameState.combo >= 3 && (() => {
                const c = gameState.combo;
                const tier = c >= 10
                  ? { label: 'GODLIKE', color: '#00FFFF' }
                  : c >= 7
                    ? { label: 'INSANE', color: '#FF1493' }
                    : c >= 5
                      ? { label: 'RAMPAGE', color: '#FF3B30' }
                      : { label: 'COMBO', color: '#FF9500' };
                return (
                  <div
                    className="hud-item text-[10px] sm:text-xs font-game flex items-center gap-2 animate-pulse"
                    style={{ borderColor: tier.color, color: tier.color, boxShadow: `0 0 8px ${tier.color}80` }}
                    data-testid="hud-combo"
                  >
                    <span>{c}x</span>
                    <span className="text-[8px] sm:text-[10px] tracking-wider">{tier.label}</span>
                  </div>
                );
              })()}
              {/* Tournament Timer */}
              {gameState.tournamentTimeRemaining !== null && (
                <div className={`hud-item text-[10px] sm:text-xs font-game ${gameState.tournamentTimeRemaining <= 10 ? 'text-[#FF3B30] animate-pulse' : 'text-[#FFD700]'}`} data-testid="hud-tournament-timer">
                  TIME: {Math.floor(gameState.tournamentTimeRemaining / 60)}:{(gameState.tournamentTimeRemaining % 60).toString().padStart(2, '0')}
                </div>
              )}
              {gameState.grenades > 0 && (
                <div className="hud-item text-[#FF9500] text-[10px] sm:text-xs" data-testid="hud-grenades">
                  NADE: {gameState.grenades}
                </div>
              )}
              {gameState.lives > 0 && (
                <div className="hud-item text-[#FF1493] text-[10px] sm:text-xs flex items-center gap-1" data-testid="hud-lives">
                  <span className="text-base leading-none">♥</span>
                  <span>x{gameState.lives}</span>
                </div>
              )}
              {/* NFT Character Indicator */}
              {nftName && (
                <div 
                  className="hud-item text-[8px] sm:text-[10px] px-2 py-1 rounded"
                  style={{ 
                    background: 'linear-gradient(90deg, rgba(153,69,255,0.3), rgba(20,241,149,0.3))',
                    border: '1px solid rgba(153,69,255,0.5)'
                  }}
                  data-testid="hud-nft"
                >
                  <span className="text-[#9945FF]">NFT:</span> <span className="text-[#14F195]">{nftName}</span>
                </div>
              )}
              {/* Multiplayer Status */}
              {isMultiplayer && (
                <div 
                  className={`hud-item text-[8px] sm:text-[10px] px-2 py-1 rounded flex items-center gap-1 ${
                    multiplayerSync.isConnected ? 'bg-[#14F195]/20 border border-[#14F195]/50' : 'bg-[#FF3B30]/20 border border-[#FF3B30]/50'
                  }`}
                  data-testid="hud-multiplayer"
                >
                  <Users className="w-3 h-3" />
                  <span className={multiplayerSync.isConnected ? 'text-[#14F195]' : 'text-[#FF3B30]'}>
                    {multiplayerSync.isConnected ? `CO-OP (${Object.keys(multiplayerSync.otherPlayers || {}).length + 1}P)` : 'CONNECTING...'}
                  </span>
                  {multiplayerSync.isConnected && (
                    <span
                      className={`ml-1 px-1.5 py-0.5 rounded font-bold ${
                        multiplayerSync.isHost
                          ? 'bg-[#FFD700] text-black'
                          : 'bg-[#9945FF]/40 text-[#E0CCFF] border border-[#9945FF]/60'
                      }`}
                      data-testid="hud-host-badge"
                      title={multiplayerSync.isHost ? 'You are the authoritative host' : 'Co-op partner — host is authoritative'}
                    >
                      {multiplayerSync.isHost ? 'HOST ⚡' : 'CO-OP'}
                    </span>
                  )}
                </div>
              )}
              {/* NFT Ability Cooldowns */}
              {nftBonuses && gameState.nftAbilities && (
                <div className="flex flex-col gap-1 mt-1" data-testid="nft-abilities-hud">
                  {/* Spawn Shield */}
                  {gameState.nftAbilities.spawnShieldActive && (
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[8px] bg-[#00FFFF]/20 border border-[#00FFFF]/50">
                      <div className="w-2 h-2 rounded-full bg-[#00FFFF] animate-pulse"></div>
                      <span className="text-[#00FFFF]">SHIELD {gameState.nftAbilities.spawnShieldRemaining}s</span>
                    </div>
                  )}
                  {/* Burn Aura */}
                  {gameState.nftAbilities.burnAuraActive && (
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[8px] bg-[#FF4500]/20 border border-[#FF4500]/50">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ 
                          backgroundColor: '#FF4500',
                          opacity: 0.5 + (gameState.nftAbilities.burnAuraCooldown / 60) * 0.5
                        }}
                      ></div>
                      <span className="text-[#FF4500]">BURN</span>
                      <div className="w-8 h-1 bg-[#333] rounded overflow-hidden">
                        <div 
                          className="h-full bg-[#FF4500] transition-all"
                          style={{ width: `${(gameState.nftAbilities.burnAuraCooldown / 60) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  {/* Slow Aura */}
                  {gameState.nftAbilities.slowAuraActive && (
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[8px] bg-[#00FFFF]/20 border border-[#87CEEB]/50">
                      <div className="w-2 h-2 rounded-full bg-[#87CEEB] animate-pulse"></div>
                      <span className="text-[#87CEEB]">FROST AURA</span>
                    </div>
                  )}
                  {/* Chain Lightning */}
                  {gameState.nftAbilities.chainLightningMax > 0 && (
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[8px] bg-[#FFFF00]/20 border border-[#FFFF00]/50">
                      <div 
                        className={`w-2 h-2 rounded-full ${gameState.nftAbilities.chainLightningReady ? 'bg-[#FFFF00] animate-ping' : 'bg-[#666]'}`}
                      ></div>
                      <span className={gameState.nftAbilities.chainLightningReady ? 'text-[#FFFF00]' : 'text-[#888]'}>
                        {gameState.nftAbilities.chainLightningReady ? 'CHAIN READY!' : `CHAIN ${gameState.nftAbilities.chainLightningCounter}/${gameState.nftAbilities.chainLightningMax}`}
                      </span>
                    </div>
                  )}
                  {/* Health Regen */}
                  {gameState.nftAbilities.healthRegenActive && (
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[8px] bg-[#228B22]/20 border border-[#228B22]/50">
                      <div className="w-2 h-2 rounded-full bg-[#228B22]"></div>
                      <span className="text-[#228B22]">REGEN</span>
                      <span className="text-[#228B22]/70">{gameState.nftAbilities.healthRegenCooldown}s</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1 sm:gap-2 items-end">
              <div className="hud-item text-[10px] sm:text-xs" data-testid="hud-kills">
                KILLS: {gameState.kills}
              </div>
              {/* Current Weapon Display */}
              {gameState.currentWeapon !== 'default' && (
                <div className={`hud-item text-[10px] sm:text-xs ${
                  gameState.currentWeapon === 'rocket_launcher' ? 'text-[#FF4444]'
                  : gameState.currentWeapon === 'assault_rifle' ? 'text-[#14F195]'
                  : 'text-[#4A90D9]'
                }`} data-testid="hud-weapon">
                  {gameState.currentWeapon === 'rocket_launcher' ? `ROCKET: ${gameState.rocketAmmo}`
                  : gameState.currentWeapon === 'assault_rifle' ? `RIFLE: ${gameState.rifleAmmo}`
                  : `TANK: ${gameState.tankAmmo}`}
                </div>
              )}
              {(gameState.rocketAmmo > 0 || gameState.tankAmmo > 0 || gameState.rifleAmmo > 0) && gameState.currentWeapon === 'default' && (
                <div className="hud-item text-[#A1A1AA] text-[8px] sm:text-[10px]" data-testid="hud-switch">
                  Q: SWITCH
                </div>
              )}
              <div className="flex gap-2">
                <button 
                  onClick={toggleMute}
                  className="hud-item cursor-pointer hover:text-white transition-colors pointer-events-auto p-1"
                  data-testid="mute-btn"
                >
                  {isMuted ? <VolumeX className="w-3 h-3 sm:w-4 sm:h-4" /> : <Volume2 className="w-3 h-3 sm:w-4 sm:h-4" />}
                </button>
                <button 
                  onClick={() => setShowSoundSettings(true)}
                  className="hud-item cursor-pointer hover:text-white transition-colors pointer-events-auto p-1"
                  data-testid="sound-settings-btn"
                >
                  <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
                <button 
                  onClick={toggleFullscreen}
                  className="hud-item cursor-pointer hover:text-white transition-colors pointer-events-auto p-1"
                  data-testid="fullscreen-btn"
                >
                  {isFullscreen ? <Minimize2 className="w-3 h-3 sm:w-4 sm:h-4" /> : <Maximize2 className="w-3 h-3 sm:w-4 sm:h-4" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Start Screen - Only shown if not auto-started and game not playing */}
        {!gameState.isPlaying && !gameState.isGameOver && !autoStart && !isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-20 p-4">
            <h2 className="font-game text-xl sm:text-2xl md:text-3xl text-[#14F195] mb-4 neon-text text-center">
              METAL LEGO
            </h2>
            
            <div className="text-[#A1A1AA] mb-6 text-center text-sm">
              {isTouchDevice ? (
                <div className="space-y-1">
                  <p>Joystick to move in all directions</p>
                  <p>JUMP button to jump</p>
                  <p>Tap FIRE to shoot | NADE for grenades</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p>Arrow Keys / WASD to move (all directions)</p>
                  <p>SHIFT / Z to jump | SPACE to shoot | G for grenade</p>
                </div>
              )}
            </div>
            
            <div className="glass-card p-3 sm:p-4 mb-6 text-xs sm:text-sm">
              <p className="text-[#14F195] font-game text-xs mb-2">POWER-UPS:</p>
              <div className="grid grid-cols-2 gap-2 text-[#A1A1AA]">
                <span><span className="text-[#14F195]">R</span> Rapid Fire</span>
                <span><span className="text-[#5AC8FA]">S</span> Shield</span>
                <span><span className="text-[#FF3B30]">+</span> Health</span>
                <span><span className="text-[#FF9500]">G</span> Grenades</span>
                <span><span className="text-[#FFCC00]">»</span> Speed</span>
              </div>
            </div>
            
            <Button
              onClick={startWithCountdown}
              className="game-btn text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4"
              data-testid="start-game-btn"
            >
              START GAME
            </Button>
          </div>
        )}

        {/* Pause Screen */}
        {gameState.isPaused && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-20">
            <h2 className="font-game text-2xl sm:text-3xl text-[#9945FF] mb-8">PAUSED</h2>
            <Button
              onClick={pauseGame}
              className="game-btn"
              data-testid="resume-game-btn"
            >
              RESUME
            </Button>
          </div>
        )}

        {/* Game Over Screen */}
        {gameState.isGameOver && !isStarting && (
          <div className="game-over-overlay p-4" data-testid="game-over-screen">
            <h2 className="font-game text-2xl sm:text-4xl text-[#FF3B30] mb-6 sm:mb-8 neon-text">
              {isTournament ? 'MATCH COMPLETE' : 'GAME OVER'}
            </h2>
            <div className="glass-card p-6 sm:p-8 mb-6 sm:mb-8 text-center">
              <p className="font-game text-lg sm:text-xl text-[#14F195] mb-4">
                {isTournament ? 'SCORE SUBMITTED' : 'FINAL SCORE'}
              </p>
              <p className="font-game text-3xl sm:text-4xl text-white mb-6">
                {gameState.score.toString().padStart(6, '0')}
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-[#A1A1AA]">
                  <p className="font-game text-xs mb-1">LEVEL</p>
                  <p className="text-white font-game">{gameState.level}</p>
                </div>
                <div className="text-[#A1A1AA]">
                  <p className="font-game text-xs mb-1">KILLS</p>
                  <p className="text-white font-game">{gameState.kills}</p>
                </div>
              </div>

              {/* Co-op per-player score breakdown + MVP banners (P-3) */}
              {isMultiplayer && (() => {
                const peers = finalPeerScores || multiplayerSync?.otherPlayers || {};
                if (Object.keys(peers).length === 0) return null;
                // Build full participants list for MVP calc
                const participants = [
                  { wallet: 'YOU', label: 'YOU', score: gameState.score, kills: gameState.kills, isLocal: true },
                  ...Object.entries(peers).map(([wallet, p]) => ({
                    wallet,
                    label: wallet.length > 10 ? `${wallet.slice(0, 4)}…${wallet.slice(-4)}` : wallet,
                    score: p.score || 0,
                    kills: p.kills || 0,
                    isLocal: false,
                  })),
                ];
                const topScorer = participants.reduce((a, b) => (b.score > a.score ? b : a));
                const mostKills = participants.reduce((a, b) => (b.kills > a.kills ? b : a));
                const totalKills = participants.reduce((sum, p) => sum + p.kills, 0);
                return (
                  <div className="mt-6 pt-4 border-t border-[#9945FF]/30" data-testid="coop-score-breakdown">
                    <p className="font-game text-[10px] text-[#9945FF] mb-3 tracking-wider">CO-OP BREAKDOWN</p>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between items-center bg-[#FFD700]/10 border border-[#FFD700]/40 rounded px-3 py-2">
                        <span className="font-game text-[10px] text-[#FFD700]">YOU</span>
                        <span className="font-game text-white">
                          {gameState.score.toString().padStart(6, '0')}
                          <span className="text-[#A1A1AA] ml-2 text-[9px]">{gameState.kills}K</span>
                        </span>
                      </div>
                      {Object.entries(peers).map(([wallet, pState]) => {
                        const short = wallet.length > 10 ? `${wallet.slice(0, 4)}…${wallet.slice(-4)}` : wallet;
                        return (
                          <div
                            key={wallet}
                            className="flex justify-between items-center bg-[#9945FF]/10 border border-[#9945FF]/40 rounded px-3 py-2"
                            data-testid={`coop-partner-row-${wallet}`}
                          >
                            <span className="font-game text-[10px] text-[#E0CCFF]" title={wallet}>{short}</span>
                            <span className="font-game text-white">
                              {(pState.score || 0).toString().padStart(6, '0')}
                              <span className="text-[#A1A1AA] ml-2 text-[9px]">{pState.kills || 0}K</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* MVP Banner (P-3) */}
                    <div className="mt-4 pt-3 border-t border-[#FFD700]/20" data-testid="mvp-banner">
                      <p className="font-game text-[10px] text-[#FFD700] mb-2 tracking-wider">⭐ MATCH MVPS</p>
                      <div className="grid grid-cols-1 gap-2 text-[10px]">
                        <div
                          className="flex justify-between items-center bg-gradient-to-r from-[#FFD700]/20 to-transparent border-l-2 border-[#FFD700] rounded px-3 py-1.5"
                          data-testid="mvp-top-scorer"
                        >
                          <span className="text-[#FFD700] font-game">🏆 TOP SCORER</span>
                          <span className="text-white font-game">{topScorer.label} · {topScorer.score.toLocaleString()}</span>
                        </div>
                        <div
                          className="flex justify-between items-center bg-gradient-to-r from-[#FF3B30]/20 to-transparent border-l-2 border-[#FF3B30] rounded px-3 py-1.5"
                          data-testid="mvp-most-kills"
                        >
                          <span className="text-[#FF3B30] font-game">💀 MOST KILLS</span>
                          <span className="text-white font-game">{mostKills.label} · {mostKills.kills}</span>
                        </div>
                        <div
                          className="flex justify-between items-center bg-gradient-to-r from-[#14F195]/20 to-transparent border-l-2 border-[#14F195] rounded px-3 py-1.5"
                          data-testid="mvp-team-effort"
                        >
                          <span className="text-[#14F195] font-game">🤝 TEAM TOTAL</span>
                          <span className="text-white font-game">{totalKills} kills</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {isTournament && (
                <p className="text-[#FFD700] font-game text-xs mt-4">Returning to bracket...</p>
              )}
            </div>
            {!isTournament && (
              <Button
                onClick={handlePlayAgain}
                className="game-btn"
                data-testid="restart-game-btn"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                PLAY AGAIN
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Mobile Pause Button - fixed position */}
      {gameState.isPlaying && !gameState.isGameOver && isTouchDevice && countdown === null && (
        <button
          onClick={pauseGame}
          className="fixed top-2 right-2 sm:top-4 sm:right-4 z-50 p-2 sm:p-3 rounded-full bg-black/50 border border-[#9945FF] text-[#9945FF]"
          data-testid="mobile-pause-btn"
        >
          <Pause className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      )}

      {/* Desktop Pause Button */}
      {gameState.isPlaying && !gameState.isGameOver && !isTouchDevice && countdown === null && (
        <div className="flex justify-center gap-4 mt-4">
          <Button
            onClick={pauseGame}
            variant="outline"
            className="border-[#9945FF] text-[#9945FF] hover:bg-[#9945FF]/10"
            data-testid="pause-game-btn"
          >
            <Pause className="w-4 h-4 mr-2" />
            {gameState.isPaused ? 'Resume' : 'Pause'}
          </Button>
        </div>
      )}

      {/* Touch Controls */}
      <TouchControls
        isVisible={showTouchControls && countdown === null}
        onMoveChange={handleMoveChange}
        onJump={handleJump}
        onShootStart={handleShootStart}
        onShootEnd={handleShootEnd}
        onGrenade={handleGrenade}
      />

      {/* Desktop Controls Guide - 2.5D Movement */}
      {!isTouchDevice && !gameState.isPlaying && !isLoading && (
        <div className="max-w-6xl mx-auto mt-6">
          <div className="glass-card p-4 flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-[#1a1a1a] border border-[#333] rounded text-[#14F195] font-mono">
                WASD
              </kbd>
              <span className="text-[#A1A1AA]">Move (all directions)</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-[#1a1a1a] border border-[#333] rounded text-[#5AC8FA] font-mono">
                SHIFT/Z
              </kbd>
              <span className="text-[#A1A1AA]">Jump</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-[#1a1a1a] border border-[#333] rounded text-[#14F195] font-mono">
                SPACE
              </kbd>
              <span className="text-[#A1A1AA]">Shoot</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-[#1a1a1a] border border-[#333] rounded text-[#FF9500] font-mono">
                G
              </kbd>
              <span className="text-[#A1A1AA]">Grenade</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-[#1a1a1a] border border-[#333] rounded text-[#14F195] font-mono">
                P/ESC
              </kbd>
              <span className="text-[#A1A1AA]">Pause</span>
            </div>
          </div>
        </div>
      )}

      {/* Cutscene Overlay */}
      {showCutscene && (
        <Cutscene 
          level={cutsceneLevel}
          onComplete={handleCutsceneComplete}
          tokenInfo={{ contract: TOKEN_CONTRACT }}
        />
      )}
      
      {/* Intro Video Overlay */}
      {showIntroVideo && (
        <div 
          className={`fixed inset-0 z-50 bg-black flex items-center justify-center cursor-pointer transition-opacity duration-500 ${
            videoFading === 'in' ? 'opacity-0 animate-fade-in' : 
            videoFading === 'out' ? 'opacity-0' : 
            'opacity-100'
          }`}
          onClick={handleSkipVideo}
          onKeyDown={(e) => e.key === 'Escape' && handleSkipVideo()}
          tabIndex={0}
          data-testid="intro-video-overlay"
        >
          <video
            ref={videoRef}
            src={INTRO_VIDEO_URL}
            className="max-w-full max-h-full object-contain"
            playsInline
            webkit-playsinline=""
            autoPlay
            muted
            preload="auto"
            onEnded={handleVideoEnd}
            onCanPlay={() => {
              // Ensure video plays on iOS
              if (videoRef.current) {
                videoRef.current.play().catch(e => console.log('Intro video play error:', e));
              }
            }}
            onError={(e) => {
              console.error('Video error:', e);
              handleVideoEnd();
            }}
            data-testid="intro-video"
          />
          
          {/* Skip hint */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-sm font-game animate-pulse">
            TAP TO SKIP
          </div>
        </div>
      )}

      {/* Sound Settings Modal */}
      <SoundSettings isOpen={showSoundSettings} onClose={() => setShowSoundSettings(false)} />
    </div>
  );
};

export default GameCanvas;
